import LoadingComponent from "../../../../../components/LoadingComponent.jsx";
import React, {useEffect, useMemo, useState} from "react";
import useAuthorityConfiguration from "../../../../../api/utilities/useAuthorityConfiguration.js";
import {ErpCalculationApi} from "../../../../../api/authority/generated/index";
import {useTranslation} from "react-i18next";
import SeeMore from "../../../../../components/SeeMore.jsx";
import toast from "react-hot-toast";
import {useErrorTranslation} from "../../../../../api/utilities/translateErrorMessage.js";
import Toggle from "../../../../../components/Toggle.jsx";
import {useRenderCurrencyValue, useRenderNumber} from "../../../../../api/utilities/useValueRenderers.js";
import useUserSettings from "../../../../../utils/useUserSettings.js";
import DataGrid2 from "../../../../../components/datagrid/DataGrid2.jsx";

const Mode = {
    Turnover: 'turnover',
    Amount: 'amount',
};

export default function DailyTopProductPerformance({title, day, project}) {
    let {t} = useTranslation();
    let {et} = useErrorTranslation();

    let [data, setData] = useState(null);
    let workerConfig = useAuthorityConfiguration();
    let renderCurrencyValue = useRenderCurrencyValue();
    let renderNumber = useRenderNumber();

    let [userSettings] = useUserSettings();

    useEffect(() => {
        new ErpCalculationApi(workerConfig)
            .dailyProductPerformance({
                projectId: project.id,
                dayInput: {
                    day: day.format('YYYY-MM-DD'),
                }
            })
            .then(setData)
            .catch(e => et(e).then(toast.error));
    }, [project.id, day, title]);

    let [mode, setMode] = useState(Mode.Amount);

    let dataSource = useMemo(() => {
        return ((mode === Mode.Turnover) ? (data?.topProductsByTurnoverGross) : (data?.topProductsByAmountGross)) ?? [];
    }, [data, mode]);

    let columns = useMemo(() => [
        {
            name: 'productName',
            header: t('orders.product-name'),
            defaultFlex: 2,
            sortable: false,
        },
        {
            name: 'soldAmount',
            header: t('orders.amount'),
            defaultFlex: 1,
            textAlign: 'end',
            sortable: false,
            render: (input) => renderNumber(
                input.data.amount,
                true,
            ),
        },
        {
            name: 'turnover',
            header: t('dashboard.turnover'),
            defaultFlex: 2,
            textAlign: 'end',
            minWidth: 100,
            sortable: false,
            render: (input) => renderCurrencyValue(
                input.data.turnover,
                true,
            ),
        },
    ], [userSettings]);

    // noinspection JSUnresolvedReference
    return (
        <LoadingComponent
            loading={data === null}
            className={'col-span-12 sm:col-span-6 text-slate-600 dark:text-slate-400'}
        >
            <div
                className="comp__roundedBox inline-flex flex-col w-full rounded-sm text-sm bg-white dark:bg-slate-800 shadow-lg border border-slate-200 dark:border-slate-700">
                <div
                    className={'px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between'}>
                    <h2 className={'font-semibold text-slate-800 dark:text-slate-100'}>
                        {title}
                    </h2>
                    <div className={'flex gap-2'}>
                        {t('orders.turnover')}
                        <Toggle enabled={mode === Mode.Amount} setEnabled={() => {
                            setMode(mode === Mode.Amount ? Mode.Turnover : Mode.Amount);
                        }}/>
                        {t('orders.amount')}
                    </div>
                </div>
                <div className={'p-3'}>
                    <SeeMore heightClass={'h-32'} active={dataSource.length > 5}>
                        <DataGrid2
                            columns={columns}
                            dataSource={dataSource}
                        />
                    </SeeMore>
                </div>
            </div>
        </LoadingComponent>
    );
}
